import React from 'react';
import { Grid, Box, Paper, IconButton, LinearProgress, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Map from '../components/Map';
import { getClientList } from '../utils/clientUtils';
import DeviceCardWrapper from '../components/DeviceCardWrapper';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

const displayMap = true;


function Dashboard() {
    const { t } = useTranslation();
    const [clientList, setClientList] = React.useState(null);
    const [devicePositions, setDevicePositions] = React.useState({});
    const [mapEnlarged, setMapEnlarged] = React.useState(false);
    const [focusedDevice, setFocusedDevice] = React.useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const mapRef = React.useRef(null);

    React.useEffect(() => {
        async function fetchClientList() {
            const clients = await getClientList();
            if (Array.isArray(clients)) {
                clients.sort((a, b) => (b.online ? 1 : 0) - (a.online ? 1 : 0));
            }
            setClientList(clients);
        }
        fetchClientList();

        const interval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchClientList();
            }
        }, 60000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchClientList();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const addDevicePosition = React.useCallback((deviceId, position) => {
        setDevicePositions(prevPositions => ({
            ...prevPositions,
            [deviceId]: position
        }));
    }, []);

    const focusDevice = React.useCallback((deviceID) => {
        setFocusedDevice(prev => prev === deviceID ? null : deviceID);
        if (isMobile && mapRef.current) {
            mapRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isMobile]);

    const visiblePositions = focusedDevice
        ? (devicePositions[focusedDevice] ? { [focusedDevice]: devicePositions[focusedDevice] } : devicePositions)
        : devicePositions;

    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, gap: 2 }}>
                <Box>
                    <Grid container spacing={2}>
                        {!clientList ? (
                            <LinearProgress color="success" sx={{ width: '100%' }} />
                        ) : clientList.length === 0 ? (
                            <Box sx={{ p: 2 }}>{t('dashboard.noClientsFound')}</Box>
                        ) : (
                            clientList.map(client => (
                                <Grid item size={12} key={client.name}>
                                    <DeviceCardWrapper client={client} deviceID={client.name} deviceType={client.dtid} addDevicePosition={addDevicePosition} focusDevice={focusDevice} focusedDevice={focusedDevice} />
                                </Grid>
                            ))
                        )}
                    </Grid>
                </Box>
                <Box ref={mapRef} sx={{ height: '50vh', position: 'relative' }}>
                    <Paper elevation={3} sx={{ height: '100%', position: 'relative' }}>
                        {displayMap && <Map devicePositions={visiblePositions} />}
                    </Paper>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                pt: 3
            }}
        >
            <Box sx={{ width: mapEnlarged ? '100vw' : '30vw', height: '100vh', position: 'relative' }} >
                <Paper elevation={3} sx={{ height: '100%', position: 'relative' }}>
                    <IconButton
                        onClick={() => setMapEnlarged(!mapEnlarged)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            zIndex: 2
                        }}
                    >
                        {mapEnlarged ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                    <Box sx={{ height: '100%' }}>
                        {displayMap && <Map devicePositions={visiblePositions} />}
                    </Box>
                </Paper>
            </Box>

            {!mapEnlarged && (
                <Box sx={{ width: '70vw', pl: 2 }}>
                    <Grid container spacing={2} >
                        {
                            !clientList ? (
                                <LinearProgress color="success"
                                    sx={{
                                        width: '100%',
                                        pt: 3,
                                        position: 'relative'
                                    }} />
                            ) : clientList.length === 0 ? (
                                <div>{t('dashboard.noClientsFound')}</div>
                            ) : (
                                clientList.map(client => (
                                    <Grid item size={6} key={client.name}>
                                        <DeviceCardWrapper client={client} deviceID={client.name} deviceType={client.dtid} addDevicePosition={addDevicePosition} focusDevice={focusDevice} focusedDevice={focusedDevice} />
                                    </Grid>
                                ))
                            )
                        }
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

export default Dashboard;
